import type { TimeEntry } from '~/types/timeEntry.type'
import type { User } from '~/types/user.type'
import { subject } from '@casl/ability'
import { addWeeks, subWeeks } from 'date-fns'

export const useTimeEntries = defineStore('time-entries', () => {
  const { user } = storeToRefs(useUserStore())
  const { can } = useAppAbility()

  const timesheetsUserIdFromStorage = useLocalStorage<'all' | User['id'] | undefined>('timesheets-user-id', undefined)

  const userQuery = useRouteQuery('userId', timesheetsUserIdFromStorage, { transform: (val) => {
    const canReadOthersTimeEntries = can('read', subject('time-entry', { userId: -1 }))
    if (val === 'all' && canReadOthersTimeEntries)
      return 'all'

    const num = Number(val)

    if (!Number.isNaN(num) && (num === user.value.id || canReadOthersTimeEntries))
      return num

    return user.value?.id ?? -1
  } })

  watch(userQuery, val => timesheetsUserIdFromStorage.value = val)

  const timeEntriesFilter = ref<ApiTimeEntryGetListParams & { withUser: true }>({
    userId: userQuery.value === 'all' ? undefined : userQuery.value,
    timeIntervalStartAt: subWeeks(Date.now(), 1),
    timeIntervalEndAt: addWeeks(Date.now(), 1),
    withUser: true,
  })

  const timeEntries = ref<TimeEntry<'withTask' | 'withProject' | 'withUser'>[]>([])

  const { data: timeEntriesData, refresh: fetchTimeEntries } = useApiTimeEntryGetList(timeEntriesFilter, { watch: false })

  watch(timeEntriesData, (timeEntriesData) => {
    timeEntries.value = timeEntriesData?.data || []
  })

  return { timeEntriesFilter, timeEntries, fetchTimeEntries, userQuery }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useTimeEntries, import.meta.hot))
